var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getUser, updateUserProfile } from 'api/requests/userApi';
import { STATUS } from 'store/statuses';
import { mapToUserProfile } from 'api/mapper/mapToProfile';
import { UserV1StatusEnum } from 'target/user';
const initialUserProfile = {
    status: UserV1StatusEnum.Active,
    username: '',
    email: '',
    locale: '',
    prefix: '',
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: '',
    countryCode: '',
    areaCode: '',
    phoneNumber: '',
    subject: '' // Track authenticated user's subject (sub claim)
};
export const fetchUserProfileThunk = createAsyncThunk('user/fetchUserProfile', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { rejectWithValue }) {
    var _b;
    try {
        const response = yield getUser();
        const user = (_b = response.data.users) === null || _b === void 0 ? void 0 : _b.at(0);
        if (!user) {
            return rejectWithValue('User not found');
        }
        return mapToUserProfile(user);
    }
    catch (error) {
        const fallbackMessage = 'Failed to fetch user profile';
        let message;
        if (error instanceof Error) {
            message = error.message;
        }
        else if (typeof error === 'string') {
            message = error;
        }
        else {
            message = fallbackMessage;
        }
        return rejectWithValue(message);
    }
}), {
    condition: (_, { getState }) => {
        const state = getState();
        // Allow re-fetches after success or error, but avoid overlapping
        // profile requests while a fetch is already in flight.
        return state.user.status !== STATUS.LOADING;
    }
});
export const updateUserProfileThunk = createAsyncThunk('user/updateUserProfile', (profile_1, _a) => __awaiter(void 0, [profile_1, _a], void 0, function* (profile, { dispatch, rejectWithValue }) {
    try {
        yield updateUserProfile(profile);
        dispatch(fetchUserProfileThunk());
    }
    catch (error) {
        const fallbackMessage = 'Failed to update user profile';
        let message;
        if (error instanceof Error) {
            message = error.message;
        }
        else if (typeof error === 'string') {
            message = error;
        }
        else {
            message = fallbackMessage;
        }
        return rejectWithValue(message);
    }
}));
const initialState = {
    profile: initialUserProfile,
    status: STATUS.IDLE,
    error: ''
};
const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setProfile(state, action) {
            state.profile = Object.assign(Object.assign({}, state.profile), action.payload);
        },
        resetUserState() {
            return initialState;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserProfileThunk.pending, (state) => {
            state.status = STATUS.LOADING;
        })
            .addCase(fetchUserProfileThunk.fulfilled, (state, action) => {
            state.status = STATUS.SUCCESS;
            // Preserve subject when merging API payload to maintain user-switch detection
            state.profile = Object.assign(Object.assign({}, action.payload), { subject: state.profile.subject });
        })
            .addCase(fetchUserProfileThunk.rejected, (state, action) => {
            var _a, _b;
            state.status = STATUS.ERROR;
            state.error = (_b = (_a = action.payload) !== null && _a !== void 0 ? _a : action.error.message) !== null && _b !== void 0 ? _b : 'Unknown error';
        });
    }
});
export const { setProfile, resetUserState } = userSlice.actions;
export default userSlice.reducer;
